/**
 * Scope Types / Organization Types Tab
 * Manages organization types used for both:
 * - Organization categories (default type for an org)
 * - Project scopes (role assignment on projects)
 */
import { useState } from 'react';
import { useScopeTypes, useDeleteScopeType, useUpdateScopeType } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tags, Plus, Edit, Trash2, ArrowUp, ArrowDown, Star, AlertCircle, AlertTriangle } from 'lucide-react';
import type { ScopeType } from '@/lib/schemas';
import { ScopeTypeModal } from './ScopeTypeModal';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function ScopeTypesTab() {
  const { data: scopeTypes, isLoading } = useScopeTypes();
  const deleteScopeType = useDeleteScopeType();
  const updateScopeType = useUpdateScopeType();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingScopeType, setEditingScopeType] = useState<ScopeType | null>(null);
  const [deleteConfirmScopeType, setDeleteConfirmScopeType] = useState<ScopeType | null>(null);

  // Handle create
  const handleCreate = () => {
    setEditingScopeType(null);
    setIsModalOpen(true);
  };

  // Handle edit
  const handleEdit = (scopeType: ScopeType) => {
    setEditingScopeType(scopeType);
    setIsModalOpen(true);
  };

  // Helper to check if scope type is protected (Issue #4)
  const isProtectedType = (scopeType: ScopeType): boolean => {
    return scopeType.is_client_type || ['Self', 'Client', 'self', 'client'].includes(scopeType.name);
  };

  // Handle delete - opens confirmation dialog
  const handleDelete = (scopeType: ScopeType) => {
    // Prevent deleting protected system types (Issue #4)
    if (isProtectedType(scopeType)) {
      toast.error(`Cannot delete protected client type '${scopeType.display_name}'. System types cannot be deleted.`);
      return;
    }
    setDeleteConfirmScopeType(scopeType);
  };

  // Handle confirmed delete
  const handleConfirmDelete = async () => {
    if (!deleteConfirmScopeType) return;
    try {
      await deleteScopeType.mutateAsync(deleteConfirmScopeType.id);
    } catch (error) {
      // Error handling already done in hook
      console.error('Delete failed:', error);
    }
    setDeleteConfirmScopeType(null);
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setDeleteConfirmScopeType(null);
  };

  // Handle reorder (move up)
  const handleMoveUp = async (scopeType: ScopeType, index: number) => {
    if (!scopeTypes || index === 0) return; // Already first

    const prevScopeType = scopeTypes[index - 1];

    try {
      // Swap display orders
      await Promise.all([
        updateScopeType.mutateAsync({
          id: scopeType.id,
          data: { display_order: prevScopeType.display_order }
        }),
        updateScopeType.mutateAsync({
          id: prevScopeType.id,
          data: { display_order: scopeType.display_order }
        })
      ]);
    } catch (error) {
      console.error('Reorder failed:', error);
    }
  };

  // Handle reorder (move down)
  const handleMoveDown = async (scopeType: ScopeType, index: number) => {
    if (!scopeTypes || index === scopeTypes.length - 1) return; // Already last

    const nextScopeType = scopeTypes[index + 1];

    try {
      // Swap display orders
      await Promise.all([
        updateScopeType.mutateAsync({
          id: scopeType.id,
          data: { display_order: nextScopeType.display_order }
        }),
        updateScopeType.mutateAsync({
          id: nextScopeType.id,
          data: { display_order: scopeType.display_order }
        })
      ]);
    } catch (error) {
      console.error('Reorder failed:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  // Sort by display_order
  const sortedScopeTypes = scopeTypes ? [...scopeTypes].sort((a, b) => a.display_order - b.display_order) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <Tags className="w-5 h-5 text-slate-600" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">Client Types</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage types used for client categories and project billing-track assignments
          </p>
        </div>
        <Button onClick={handleCreate} className="inline-flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Type
        </Button>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-blue-900">Client Types are used in two places:</p>
            <ul className="list-disc list-inside text-blue-800 mt-2 space-y-1">
              <li>As <strong>client categories</strong> when adding/editing clients</li>
              <li>As <strong>billing-track types</strong> when assigning clients to projects</li>
            </ul>
            <p className="text-xs text-sky-700 mt-2">
              Managing types here keeps everything consistent across your system.
            </p>
          </div>
        </div>
      </div>

      {/* Scope Types List */}
      {sortedScopeTypes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Tags className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No client types yet</p>
          <p className="text-sm text-gray-500 mt-1">Add your first type to get started</p>
          <Button onClick={handleCreate} className="mt-4 inline-flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add First Type
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedScopeTypes.map((scopeType, index) => (
            <div
              key={scopeType.id}
              className="border border-gray-200 rounded-lg p-4 bg-white hover:border-purple-300 transition-colors"
            >
              <div className="flex items-center justify-between gap-4">
                {/* Type Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {scopeType.is_client_type && (
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" title="Client Type" />
                    )}
                    <h3 className="font-semibold text-gray-900 truncate">
                      {scopeType.display_name}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Slug: <span className="font-mono text-xs">{scopeType.name}</span>
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {/* Edit */}
                  <Button
                    onClick={() => handleEdit(scopeType)}
                    variant="outline"
                    size="sm"
                    className="inline-flex items-center gap-1.5"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    Edit
                  </Button>

                  {/* Delete */}
                  <Button
                    onClick={() => handleDelete(scopeType)}
                    variant="outline"
                    size="sm"
                    disabled={isProtectedType(scopeType) || deleteScopeType.isPending}
                    className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                    title={isProtectedType(scopeType) ? 'Cannot delete protected system type' : 'Delete'}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </Button>

                  {/* Move Up */}
                  <Button
                    onClick={() => handleMoveUp(scopeType, index)}
                    variant="outline"
                    size="sm"
                    disabled={index === 0 || updateScopeType.isPending}
                    className="w-9 h-9 p-0"
                    title="Move up"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>

                  {/* Move Down */}
                  <Button
                    onClick={() => handleMoveDown(scopeType, index)}
                    variant="outline"
                    size="sm"
                    disabled={index === sortedScopeTypes.length - 1 || updateScopeType.isPending}
                    className="w-9 h-9 p-0"
                    title="Move down"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <ScopeTypeModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingScopeType(null);
        }}
        scopeType={editingScopeType}
        existingScopeTypes={sortedScopeTypes}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmScopeType} onOpenChange={(open) => !open && handleCancelDelete()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Client Type
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to delete "{deleteConfirmScopeType?.display_name}"?</p>
              <p className="text-sm text-muted-foreground">
                This cannot be undone. Make sure no projects are using this type.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteScopeType.isPending}
            >
              {deleteScopeType.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
